import { useCallback, useEffect, useRef, useState } from 'react'
import { Palette, Check } from 'iconoir-react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/hooks/useTheme'

// Header theme menu: a palette button that opens the list of themes, the same shape as the Cortex picker.
// A theme the package lists but has not made available is shown and cannot be chosen.
// Light and dark stay on the sun and moon button beside it.
export function ThemePicker() {
  const { picker, setThemeId } = useTheme()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  const close = useCallback((returnFocus) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const items = menuRef.current?.querySelectorAll('[data-selectable="true"]')
    const selected = menuRef.current?.querySelector('[data-selectable="true"][aria-checked="true"]')
    ;(selected || items?.[0])?.focus()
    const onPointerDown = (event) => { if (!rootRef.current?.contains(event.target)) close(false) }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open, close])

  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const items = Array.from(menuRef.current?.querySelectorAll('[data-selectable="true"]') ?? [])
    if (!items.length) return
    const at = items.indexOf(document.activeElement)
    const step = event.key === 'ArrowDown' ? 1 : -1
    items[at === -1 ? 0 : (at + step + items.length) % items.length].focus()
  }

  const choose = (id) => { setThemeId(id); close(true) }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        aria-label="Theme"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="p-2 text-(--muted-foreground) hover:text-(--accent) transition-colors duration-200"
      >
        <Palette width={20} height={20} strokeWidth={1.5} aria-hidden="true" focusable="false" />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Theme"
          onKeyDown={onKeyDown}
          className="absolute right-0 top-full mt-2 z-100 w-52 max-h-[70vh] overflow-y-auto rounded-xl border border-(--border) bg-(--card) text-(--card-foreground) shadow-card p-1"
        >
          {picker.themes.map((theme) => theme.selectable ? (
            <button
              key={theme.id}
              type="button"
              role="menuitemradio"
              aria-checked={theme.selected}
              data-selectable="true"
              tabIndex={-1}
              onClick={() => choose(theme.id)}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm',
                'hover:bg-(--muted) focus-visible:bg-(--muted) focus-visible:outline-none',
                theme.selected ? 'font-semibold text-(--foreground)' : 'text-(--card-foreground)'
              )}
            >
              <span>{theme.label}</span>
              {theme.selected && <Check width={16} height={16} strokeWidth={2} className="text-(--accent)" aria-hidden="true" />}
            </button>
          ) : (
            <div
              key={theme.id}
              role="menuitem"
              aria-disabled="true"
              tabIndex={-1}
              className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-(--muted-foreground) opacity-60"
            >
              <span>{theme.label}</span>
              <span className="text-xs">{theme.note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
